import {
  getBanksAdd,
  getBanksDelete,
  getBanksUpdate,
  getEwalletsAdd,
  getEwalletsDelete,
  getEwalletsUpdate,
  getFullNameReceiver,
} from 'components/FormEditReceiver/func';
import { BUSINESS_OF_RECEIVER_TYPE, FEMALE, MALE } from 'constants';
import { checkMaintenance } from 'helpers/maintenance';
import useFormAddReceiverStore from 'hooks/form-add-receiver/useFormAddReceiverStore';
import { isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import useReceiverAPI from './useReceiverAPI';
import useUpdateReceiverStore from './useUpdateReceiverStore';

const useUpdateReceiver = () => {
  const {
    getReceiverRequest,
    validatePhoneRequest,
    updateReceiverNameGenderRequest,
    updateReceiverMobilePhoneNumberRequest,
    updateReceiverRelationshipRequest,
    updateReceiverAddressRequest,
    updateReceiverBankRequest,
    updateReceiverEwalletRequest,
  } = useReceiverAPI();
  const {
    currentReceiver,
    setCurrentReceiver,
    configRelationship,
    configRelationshipOfBusiness,
  } = useFormAddReceiverStore();
  const {
    editReceiverInformation,
    isPersonalDetailsInformationVerified,
    isPersonalDetailsNameChanged,
    isPersonalDetailsGenderChanged,
    isPersonalDetailsMobileChanged,
    isPersonalDetailsPhoneChanged,
    isPersonalDetailsRelationshipChanged,
    isResidentialAddressStateChanged,
    isResidentialAddressDistrictChanged,
    isResidentialAddressSuburbChanged,
    isResidentialAddressPostalCodeChanged,
    isAccountDetailsBanksChanged,
    isAccountDetailsEwalletsChanged,
    checkAccountDetailsBanksChange,
    checkAccountDetailsEwalletsChange,
  } = useUpdateReceiverStore();

  const { receiverId } = useParams();

  const reCheckMobilePhoneNumberFn = async ({
    countryCode = '',
    mobileConfig = {},
    mobileNumber = '',
    phoneConfig = {},
    phoneNumber = '',
  } = {}) => {
    if (isEmpty(countryCode)) return null;
    if (isPersonalDetailsInformationVerified) return null;

    const promises = [];

    if (mobileNumber && mobileConfig?.visible && mobileConfig?.apiEnabled) {
      promises.push(
        validatePhoneRequest({ countryCode, number: mobileNumber }),
      );
    }

    if (phoneNumber && phoneConfig?.visible && phoneConfig?.apiEnabled) {
      promises.push(validatePhoneRequest({ countryCode, number: phoneNumber }));
    }

    console.log(
      `⭐ useUpdateReceiver 👉 reCheckMobilePhoneNumberFn ➡️ promises ↩`,
      promises,
    );

    if (isEmpty(promises)) return null;

    return Promise.all(promises);
  };

  const getReceiverInformationFn = async () => {
    try {
      const dataReceiver = await getReceiverRequest({ receiverId });

      if (!dataReceiver) return;

      let {
        firstname,
        lastname,
        gender,
        mobile,
        phone,
        address,
        banks,
        ewallets,
        favorited,
        relationship,
      } = dataReceiver;
      /* TEST CASES */
      /* 1. Update receiver is missing gender - BEGIN */
      // gender = '';
      /* ----------END---------- */

      const isReceiverOfBusinessType = gender === BUSINESS_OF_RECEIVER_TYPE;
      const isReceiverOfIndividualType = gender === MALE || gender === FEMALE;
      const newInformation = {
        receiverId,
        firstName: firstname,
        lastName: lastname,
        fullName: getFullNameReceiver({
          isReceiverOfBusinessType,
          isReceiverOfIndividualType,
          isReceiverTypeUnidentified: isEmpty(gender),
          lastName: lastname,
          firstName: firstname,
          countryCode: address?.country?.code,
        }),
        country: address?.country,
        countryName: address?.country?.name,
        gender,
        address,
        fullAddress: address?.fullAddress,
        mobile,
        mobileNumber: mobile?.number,
        phone,
        phoneNumber: phone?.number,
        banks,
        ewallets,
        favorited,
        relationship,
      };

      console.log(
        `⭐ useUpdateReceiver 👉 getReceiverInformationFn ➡️ newInformation ↩`,
        newInformation,
      );
      setCurrentReceiver(newInformation);
    } catch (error) {
      checkMaintenance(error);
      console.error(
        `🔥 useUpdateReceiver ❌ getReceiverInformationFn ➡️ ERROR ↩`,
        error?.message,
      );
    }
  };

  const getReceiverRelationship = ({
    value,
    isReceiverOfIndividualType,
    isReceiverOfBusinessType,
  } = {}) => {
    if (!isReceiverOfIndividualType && !isReceiverOfBusinessType) return '';
    if (isReceiverOfIndividualType)
      return configRelationship?.find((r) => r?.key === value) || '';
    if (isReceiverOfBusinessType)
      return configRelationshipOfBusiness?.find((r) => r?.key === value) || '';
    return '';
  };

  const onSaveUpdateReceiverInformation = (updateReceiverPayload) => {
    const {
      firstname,
      lastname,
      gender,
      mobile,
      phone,
      relationship,
      address,
      banks,
      ewallets,
    } = updateReceiverPayload || {};

    let promises = [];

    if (isPersonalDetailsNameChanged || isPersonalDetailsGenderChanged) {
      console.log(
        `⭐ useUpdateReceiver 👉 onSaveUpdateReceiverInformation ➡️ personalDetails ➡️ updateReceiverNameGenderRequest UPDATE ↩`,
      );
      promises.push(
        updateReceiverNameGenderRequest({
          receiverId,
          firstname,
          lastname,
          gender,
        }),
      );
    }
    if (isPersonalDetailsMobileChanged) {
      console.log(
        `⭐ useUpdateReceiver 👉 onSaveUpdateReceiverInformation ➡️ personalDetails ➡️ updateReceiverMobilePhoneNumberRequest UPDATE 1 ↩`,
      );
      promises.push(
        updateReceiverMobilePhoneNumberRequest({
          receiverId,
          mobilePhoneNumber: mobile,
          type: 'mobile',
        }),
      );
    }
    if (isPersonalDetailsPhoneChanged) {
      console.log(
        `⭐ useUpdateReceiver 👉 onSaveUpdateReceiverInformation ➡️ personalDetails ➡️ updateReceiverMobilePhoneNumberRequest UPDATE 2 ↩`,
      );
      promises.push(
        updateReceiverMobilePhoneNumberRequest({
          receiverId,
          mobilePhoneNumber: phone,
          type: 'phone',
        }),
      );
    }
    if (isPersonalDetailsRelationshipChanged) {
      console.log(
        `⭐ useUpdateReceiver 👉 onSaveUpdateReceiverInformation ➡️ personalDetails ➡️ updateReceiverRelationshipRequest UPDATE ↩`,
      );
      promises.push(
        updateReceiverRelationshipRequest({
          receiverId,
          relationship,
        }),
      );
    }

    if (
      isResidentialAddressDistrictChanged ||
      isResidentialAddressStateChanged ||
      isResidentialAddressSuburbChanged
    ) {
      console.log(
        `⭐ useUpdateReceiver 👉 onSaveUpdateReceiverInformation ➡️ residentialAddress ➡️ updateReceiverAddressRequest UPDATE ↩`,
      );
      promises.push(
        updateReceiverAddressRequest({
          receiverId,
          address,
        }),
      );
    }

    if (isAccountDetailsBanksChanged || checkAccountDetailsBanksChange(banks)) {
      console.log(
        `⭐ useUpdateReceiver 👉 onSaveUpdateReceiverInformation ➡️ accountDetails ➡️ updateReceiverBankRequest UPDATE ↩`,
      );
      const banksAdd = getBanksAdd(banks, currentReceiver?.banks);
      const banksUpdate = getBanksUpdate(banks, currentReceiver?.banks);
      const banksDelete = getBanksDelete(currentReceiver?.banks, banks);
      const bankAddPromises = banksAdd.map((bank) =>
        updateReceiverBankRequest({
          receiverId,
          bank,
          type: 'add',
        }),
      );
      const bankUpdatePromises = banksUpdate.map((bank) =>
        updateReceiverBankRequest({
          receiverId,
          bank,
          type: 'update',
        }),
      );
      const bankDeletePromises = banksDelete.map((bank) =>
        updateReceiverBankRequest({
          receiverId,
          bank,
          type: 'delete',
        }),
      );
      promises = [
        ...promises,
        ...(bankAddPromises || []),
        ...(bankUpdatePromises || []),
        ...(bankDeletePromises || []),
      ];
    }
    if (
      isAccountDetailsEwalletsChanged ||
      checkAccountDetailsEwalletsChange(ewallets)
    ) {
      console.log(
        `⭐ useUpdateReceiver 👉 onSaveUpdateReceiverInformation ➡️ accountDetails ➡️ updateReceiverEwalletRequest UPDATE ↩`,
      );
      const ewalletsAdd = getEwalletsAdd(ewallets, currentReceiver?.ewallets);
      const ewalletsUpdate = getEwalletsUpdate(
        ewallets,
        currentReceiver?.ewallets,
      );
      const ewalletsDelete = getEwalletsDelete(
        currentReceiver?.ewallets,
        ewallets,
      );
      const ewalletAddPromises = ewalletsAdd.map((ewallet) =>
        updateReceiverEwalletRequest({
          receiverId,
          ewallet,
          type: 'add',
        }),
      );
      const ewalletUpdatePromises = ewalletsUpdate.map((ewallet) =>
        updateReceiverEwalletRequest({
          receiverId,
          ewallet,
          type: 'update',
        }),
      );
      const ewalletDeletePromises = ewalletsDelete.map((ewallet) =>
        updateReceiverEwalletRequest({
          receiverId,
          ewallet,
          type: 'delete',
        }),
      );
      promises = [
        ...promises,
        ...(ewalletAddPromises || []),
        ...(ewalletUpdatePromises || []),
        ...(ewalletDeletePromises || []),
      ];
    }

    return Promise.all(promises);
  };

  return {
    reCheckMobilePhoneNumberFn,
    getReceiverInformationFn,
    getReceiverRelationship,
    onSaveUpdateReceiverInformation,
  };
};

export default useUpdateReceiver;
